// homeNav — shared destination list + per-user layout helpers for the app's
// mobile bottom navigation (BottomNav) and the redesigned home. One source of
// truth so the bar and the home page agree on boards, defaults, and the user's
// saved picks (localStorage, per user). Jun 17, 2026.

export type Dest = { icon: string; label: string; short: string; to: string; roles: string[]; ext?: boolean; excludeEmails?: string[] };

export const DAILY_PLANS_TO = "/canvas/manager-audit?mode=today";

export const DESTS: Dest[] = [
  { icon: "📊", label: "My Pipeline", short: "Pipeline", to: "/my-pipeline", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director", "qc", "roof_director", "director_of_operations"] },
  { icon: "🚚", label: "Dispatch Board", short: "Dispatch", to: "/dispatch", roles: ["admin", "roof_team_manager", "roof_specialist", "asm", "sales_director", "qc", "roof_director", "director_of_operations"] },
  { icon: "📋", label: "Roof Master", short: "Roof Master", to: "/roof-master", roles: ["admin", "roof_team_manager", "roof_specialist", "asm", "sales_director", "qc", "roof_director", "director_of_operations"] },
  { icon: "✈️", label: "Drone Inspection", short: "Drone", to: "/drone", roles: ["admin", "drone_pilot", "sales_director", "asm", "report_specialist", "qc", "roof_director", "director_of_operations", "office", "team_lead"] },
  { icon: "✅", label: "ASM Approvals", short: "Approvals", to: "/asm", roles: ["admin", "asm", "sales_director"] },
  { icon: "🔎", label: "QC & Reinspection", short: "QC", to: "/qc", roles: ["admin", "qc", "roof_director", "director_of_operations", "sales_director", "report_specialist"] },
  { icon: "💵", label: "Finance", short: "Finance", to: "/finance", roles: ["admin", "finance", "sales_director"] },
  { icon: "💹", label: "Financials", short: "P&L", to: "/financials", roles: ["admin", "finance", "sales_director"], excludeEmails: ["jcohan@bardinroofing.com"] },
  { icon: "🧮", label: "Job Analysis", short: "Jobs", to: "/jobs", roles: ["admin", "finance", "sales_director"], excludeEmails: ["jcohan@bardinroofing.com"] },
  { icon: "🤝", label: "Referrals", short: "Referrals", to: "/referrals", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director", "finance", "roof_director", "director_of_operations", "office"] },
  { icon: "🛠", label: "Ops Requests", short: "Requests", to: "/requests", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director", "roof_director", "director_of_operations", "roof_team_manager"] },
  { icon: "📞", label: "Call List", short: "Call List", to: "/call-list", roles: ["admin", "sales_director", "asm", "team_lead", "restore_manager", "director_of_operations", "roof_director", "office"] },
  { icon: "📣", label: "Insufficient Damage Outreach", short: "Outreach", to: "/outreach", roles: ["admin", "office", "qc", "roof_director", "director_of_operations", "sales_director"] },
  { icon: "💼", label: "My Business", short: "My Biz", to: "/my-business", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director"] },
  // Oct 29, 2026: every role (Greg: "a resource for all to see"). build_calendar_jobs answers any active team member.
  { icon: "📅", label: "Build Calendar", short: "Builds", to: "/build-calendar", roles: ["admin", "sales_director", "asm", "team_lead", "restore_manager", "roof_specialist", "roof_team_manager", "qc", "finance", "drone_pilot", "report_specialist", "office", "canvasser", "roof_director", "director_of_operations", "project_manager"] },
  // Oct 19, 2026: My Pay — paid history, every statement, the goal slider.
  { icon: "💰", label: "My Pay", short: "My Pay", to: "/my-pay", roles: ["restore_manager", "team_lead", "asm", "roof_specialist", "roof_team_manager", "sales_director", "admin", "finance", "director_of_operations", "canvasser"] },
  { icon: "▶️", label: "Run the Play", short: "Play", to: "/run-the-play", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director", "qc", "roof_director", "director_of_operations"] },
  // Oct 27, 2026: canvassers land on home now, so Canvas and Crew are in their bar.
  { icon: "🗺️", label: "Canvas", short: "Canvas", to: "/canvas", roles: ["admin", "asm", "team_lead", "sales_director", "roof_director", "director_of_operations", "restore_manager", "roof_team_manager", "qc", "finance", "office", "drone_pilot", "report_specialist", "project_manager", "roof_specialist", "canvasser"] },
  // Oct 2, 2026: the Tue–Fri 10am confirmation board (Canvas Manager Audit → ✅ Today).
  { icon: "✅", label: "Daily Plans", short: "Daily Plans", to: DAILY_PLANS_TO, roles: ["admin", "asm", "team_lead", "sales_director", "roof_director", "director_of_operations"] },
  { icon: "📋", label: "Canvas Manager Audit", short: "Canvas Audit", to: "/canvas/manager-audit", roles: ["admin", "asm", "team_lead", "sales_director", "roof_director", "director_of_operations"] },
  { icon: "🏆", label: "Crew Leaderboard", short: "Crew", to: "/crew", roles: ["admin", "restore_manager", "roof_specialist", "team_lead", "asm", "sales_director", "roof_director", "director_of_operations", "roof_team_manager", "qc", "finance", "office", "drone_pilot", "report_specialist", "canvasser"] },
  { icon: "🔔", label: "Notifications", short: "Alerts", to: "/notifications", roles: ["admin", "restore_manager", "team_lead", "asm", "sales_director", "qc", "roof_director", "director_of_operations", "finance", "roof_team_manager"] },
  { icon: "🧰", label: "PM Board", short: "PM Board", to: "/pm-board", roles: ["admin", "project_manager", "roof_team_manager", "sales_director", "director_of_operations", "finance", "qc", "roof_director", "office"] },
  // Oct 19, 2026: finance (Shannon) gets Admin for the Team Roster, which she edits — see lib/adminAccess.ts.
  { icon: "⚙️", label: "Admin", short: "Admin", to: "/admin", roles: ["admin", "sales_director", "director_of_operations", "finance"] },
];

export const MAX_PINS = 4;
export const DEFAULT_PINS_COMPANY = ["/my-pipeline", "/dispatch", "/finance", "/crew"];
// Oct 19, 2026: My Pay takes the field bar's free fourth slot (RMs).
export const DEFAULT_PINS_FIELD = ["/my-pipeline", "/canvas", "/crew", "/my-pay"];

// Role-specific bar defaults — each specialized field role gets their primary
// board front-and-center. Falls back to company/field defaults below.
// Aug 24, 2026: asm + team_lead entries added — the ASM previously fell to
// DEFAULT_PINS_COMPANY, whose /finance entry isn't in the ASM's allowed DESTS,
// so computePinned silently dropped it and the bar showed only 3 pins.
const DEFAULT_PINS_BY_ROLE: Record<string, string[]> = {
  // Sep 25, 2026: Requests replaces Calendar in Robert's bar (Calendar stays
  // one tap away in More) — the Ops Requests queue is where reps' asks land.
  roof_team_manager: ["/dispatch", "/roof-master", "/requests", "/crew"], // Robert
  roof_specialist: ["/dispatch", "/canvas", "/crew", "/roof-master"],   // Sam / Maverick (RS)
  report_specialist: ["/drone", "/qc", "/crew", "/notifications"],         // Jeremy (reports)
  drone_pilot: ["/drone", "/canvas", "/crew"],
  canvasser: ["/canvas", "/crew", "/my-pay"],
  project_manager: ["/pm-board", "/canvas", "/crew"],            // Ramiro
  // Oct 2, 2026: Daily Plans takes Canvas's slot (Canvas keeps its header chip).
  asm: [DAILY_PLANS_TO, "/my-pipeline", "/asm", "/crew"],              // Alex
  team_lead: [DAILY_PLANS_TO, "/my-pipeline", "/crew", "/run-the-play"],
};

/**
 * Pins a role always gets, first, even when the person has customized their
 * bar (Greg, Oct 2, 2026: the TL/ASM must be one tap from the daily 10am
 * confirmations). They can't unpin these; everything else stays theirs.
 */
const REQUIRED_PINS_BY_ROLE: Record<string, string[]> = {
  asm: [DAILY_PLANS_TO],
  team_lead: [DAILY_PLANS_TO],
};
export function requiredPinsFor(role: string | null | undefined): string[] {
  return (role && REQUIRED_PINS_BY_ROLE[role]) || [];
}

export function defaultPinsFor(role: string | null | undefined, isCompanyView: boolean): string[] {
  return (role && DEFAULT_PINS_BY_ROLE[role]) || (isCompanyView ? DEFAULT_PINS_COMPANY : DEFAULT_PINS_FIELD);
}

// Roles that open straight to their primary board instead of a home dashboard.
// Keyed by team_roster.role (Sam's primary is roof_specialist; Jeremy's is
// report_specialist — both still navigate anywhere via the bar).
// Oct 27, 2026: canvassers no longer land on /canvas — they get their own home
// (components/canvasser/CanvasserHome): areas, Log a lead, My Pay, their
// numbers. The full-screen canvas has no header, so they couldn't sign out.
export const LANDING_BY_ROLE: Record<string, string> = {
  roof_specialist: "/dispatch",
  report_specialist: "/drone",
  drone_pilot: "/drone",
  project_manager: "/pm-board",
};

export const COMPANY_VIEW_ROLES = new Set([
  "admin", "sales_director", "qc", "roof_director",
  "director_of_operations", "roof_team_manager", "asm",
]);

export interface HomeLayout { pinned: string[]; order: string[]; hidden: string[]; }

export function isCompanyViewRole(role: string | null | undefined, isSuperUser: boolean): boolean {
  return !!isSuperUser || (!!role && COMPANY_VIEW_ROLES.has(role));
}

export function filterDests(role: string | null | undefined, isSuperUser: boolean, secondary?: string | null, email?: string | null): Dest[] {
  const em = (email ?? "").toLowerCase();
  return DESTS.filter((d) =>
    // Per-person carve-outs beat the super-user bypass (Aug 21: Jeremy off /financials).
    !(em && d.excludeEmails?.includes(em)) &&
    (isSuperUser || (!!role && d.roles.includes(role)) || (!!secondary && d.roles.includes(secondary))),
  );
}

export function loadLayout(userId: string): HomeLayout {
  try {
    const raw = localStorage.getItem(`bp_home_layout_${userId}`);
    if (raw) { const p = JSON.parse(raw); return { pinned: p.pinned ?? [], order: p.order ?? [], hidden: p.hidden ?? [] }; }
  } catch { /* ignore */ }
  return { pinned: [], order: [], hidden: [] };
}

export function persistLayout(userId: string, l: HomeLayout) {
  try { localStorage.setItem(`bp_home_layout_${userId}`, JSON.stringify(l)); } catch { /* ignore */ }
}

export function computePinned(layout: HomeLayout, dests: Dest[], defaultTos: string[], requiredTos: string[] = []): Dest[] {
  const def = defaultTos.filter((k) => dests.some((d) => d.to === k));
  const chosen = (layout.pinned.length ? layout.pinned : def).filter((k) => dests.some((d) => d.to === k));
  const req = requiredTos.filter((k) => dests.some((d) => d.to === k));
  const eff = [...req, ...chosen.filter((k) => !req.includes(k))].slice(0, MAX_PINS);
  return eff.map((k) => dests.find((d) => d.to === k)).filter(Boolean) as Dest[];
}

export function computeRest(layout: HomeLayout, dests: Dest[], pinnedTos: string[], includeHidden: boolean): Dest[] {
  const set = new Set(pinnedTos);
  const idx = (k: string) => { const i = layout.order.indexOf(k); return i === -1 ? 999 : i; };
  const r = dests.filter((d) => !set.has(d.to)).sort((a, b) => idx(a.to) - idx(b.to));
  return includeHidden ? r : r.filter((d) => !layout.hidden.includes(d.to));
}
